/**
 * JACK-1A — niveles de pozo, orden público y proyección sanitizada.
 *
 * Los cuatro acumulados (`grand|major|minor|mini`) NO son cuatro pozos con
 * nombres bonitos: son una identidad durable que gobierna el orden en que se
 * muestran, la unicidad dentro de una colección y —cuando JACK-1B/C lo
 * conecten— el reparto del aporte. Este módulo es la SSOT de esa identidad.
 *
 * Vive junto al contrato del pozo porque lo consumen los dos lados: el backend
 * al validar y proyectar, y el admin al editar. Un enum copiado en una ruta o
 * en un front es exactamente cómo se desincronizan.
 *
 * Los pozos anteriores NO tienen nivel y eso es válido: `null` significa
 * "configuración multinivel todavía no completada". No es una modalidad
 * aparte ni se les fabrica uno: siguen operando igual hasta que el operador
 * la complete desde el admin.
 */

// Orden de presentación, de mayor a menor. NO es alfabético ni por monto: el
// jugador espera ver siempre el gordo arriba, aunque hoy pague menos que el mini.
const NIVELES_DE_POZO = ['grand', 'major', 'minor', 'mini'];

const ESTRATEGIAS_DE_DISTRIBUCION = ['uniforme', 'sesgada_al_techo'];

// Campos del modelo que CONFIGURA el admin. El CRUD de pozos los persiste a
// partir del payload (o del valor existente, o del default del contrato).
const CAMPOS_CONFIGURABLES_DEL_MODELO_DE_NIVELES = [
    'nivel',
    'porcentaje_aporte_visible',
    'porcentaje_aporte_reserva',
    'estrategia_distribucion',
    'sesgo_distribucion',
];

// Saldos del modelo: dinero en centimos que solo mueve el motor (aporte,
// disparo, reset). El CRUD de configuracion NUNCA los escribe desde un
// payload: un request admin no puede reescribir la reserva ni el overflow.
const CAMPOS_DE_SALDO_DEL_MODELO_DE_NIVELES = ['reserva_actual', 'overflow_actual'];

// Estado del modelo mientras la configuracion multinivel NO esta completada
// (`nivel` NULL): exactamente el comportamiento vigente, todo el aporte va al
// monto visible y el umbral se sortea uniforme. Es la SSOT de los defaults del
// contrato y del CHECK de la migracion: sin nivel, nada mas puede cambiar.
const MODELO_NEUTRO_SIN_NIVEL = Object.freeze({
    porcentaje_aporte_visible: 0,
    porcentaje_aporte_reserva: 0,
    estrategia_distribucion: 'uniforme',
    sesgo_distribucion: 1.0,
});

// Propuesta A, aprobada como configuracion de REFERENCIA para la coleccion de
// la rueda mini (escalera x10, aporte 0,25 % + 0,25 %, rango 2-6x, RTP 2,00 pt).
// Nunca se aplica sola: el admin la ofrece como preset y el operador decide.
// Montos en centimos.
const PRESET_DE_REFERENCIA_PROPUESTA_A = Object.freeze({
    grand: Object.freeze({
        pozo_base: 500_000,
        min_mult: 2,
        max_mult: 6,
        porcentaje_aporte_defecto: 0.5,
        porcentaje_aporte_visible: 0.25,
        porcentaje_aporte_reserva: 0.25,
        estrategia_distribucion: 'sesgada_al_techo',
        sesgo_distribucion: 3,
    }),
    major: Object.freeze({
        pozo_base: 50_000,
        min_mult: 2,
        max_mult: 6,
        porcentaje_aporte_defecto: 0.5,
        porcentaje_aporte_visible: 0.25,
        porcentaje_aporte_reserva: 0.25,
        estrategia_distribucion: 'sesgada_al_techo',
        sesgo_distribucion: 2,
    }),
    minor: Object.freeze({
        pozo_base: 5_000,
        min_mult: 2,
        max_mult: 6,
        porcentaje_aporte_defecto: 0.5,
        porcentaje_aporte_visible: 0.25,
        porcentaje_aporte_reserva: 0.25,
        estrategia_distribucion: 'uniforme',
        sesgo_distribucion: 1,
    }),
    mini: Object.freeze({
        pozo_base: 500,
        min_mult: 2,
        max_mult: 6,
        porcentaje_aporte_defecto: 0.5,
        porcentaje_aporte_visible: 0.25,
        porcentaje_aporte_reserva: 0.25,
        estrategia_distribucion: 'uniforme',
        sesgo_distribucion: 1,
    }),
});

/** Posición de un nivel en el orden público; los legacy van después de todos. */
function _rangoDelNivel(nivel) {
    const indice = NIVELES_DE_POZO.indexOf(nivel);
    return indice === -1 ? NIVELES_DE_POZO.length : indice;
}

/**
 * Ordena pozos para mostrarlos: grand → major → minor → mini → legacy.
 *
 * El desempate entre legacy es por nombre y luego por id, para que el orden no
 * dependa del azar del SELECT ni cambie entre dos llamadas: una lista que se
 * reordena sola parece un pozo que cambió de lugar.
 *
 * No muta la entrada.
 */
function ordenarPozosPorNivel(pozos) {
    if (!Array.isArray(pozos)) return [];
    return [...pozos].sort((a, b) => {
        const rangoA = _rangoDelNivel(a?.nivel);
        const rangoB = _rangoDelNivel(b?.nivel);
        if (rangoA !== rangoB) return rangoA - rangoB;
        const nombreA = String(a?.pozo_nombre ?? a?.nombre ?? '');
        const nombreB = String(b?.pozo_nombre ?? b?.nombre ?? '');
        if (nombreA !== nombreB) return nombreA < nombreB ? -1 : 1;
        return Number(a?.pozo_id ?? a?.id ?? 0) - Number(b?.pozo_id ?? b?.id ?? 0);
    });
}

/**
 * Exige que una colección (un `gameTypeCode` + `sorteoIdentificador`) no tenga
 * dos pozos del mismo nivel: dos "grand" en la misma rueda no significan nada.
 *
 * La unicidad es POR COLECCIÓN, no global: otra configuración puede tener su
 * propio grand/major/minor/mini, y por eso no se impone con un UNIQUE sobre
 * `pozos_comunes`.
 *
 * Los legacy sin nivel no cuentan: son la situación normal de lo que ya existe.
 *
 * @throws {TypeError} si hay un nivel repetido.
 */
function exigirNivelesUnicosEnLaColeccion(pozosDeLaColeccion) {
    if (!Array.isArray(pozosDeLaColeccion)) return;
    const vistos = new Set();
    for (let i = 0; i < pozosDeLaColeccion.length; i++) {
        const nivel = pozosDeLaColeccion[i]?.nivel;
        if (!nivel) continue;
        if (vistos.has(nivel)) {
            throw new TypeError(
                `[NivelesDePozo] la colección ya tiene un pozo de nivel "${nivel}": los cuatro niveles son únicos por sorteo`
            );
        }
        vistos.add(nivel);
    }
}

/**
 * Exige un nivel real. Un pozo NUEVO nace siempre con uno de los cuatro; el
 * `null` solo vale para los pozos anteriores a la configuración multinivel.
 *
 * @throws {TypeError} si el nivel no es uno de NIVELES_DE_POZO.
 */
function exigirNivelDePozo(nivel) {
    if (!NIVELES_DE_POZO.includes(nivel)) {
        throw new TypeError(
            `[NivelesDePozo] nivel "${nivel}" inválido: un pozo se crea o edita con uno de ${NIVELES_DE_POZO.join('|')}`
        );
    }
}

/**
 * Un pozo tiene la configuración multinivel COMPLETA cuando tiene nivel. Los
 * pozos anteriores nacen con `nivel` NULL: siguen operando igual que hoy y el
 * admin los muestra como "configuración multinivel pendiente".
 */
function configuracionMultinivelEstaCompleta(pozo) {
    return NIVELES_DE_POZO.includes(pozo?.nivel);
}

/**
 * Exige que un modelo SIN nivel sea exactamente el neutro: la configuración
 * multinivel se completa entera o no se toca. Un split, una estrategia o un
 * sesgo sin nivel sería una migración parcial, y eso no existe.
 *
 * @throws {TypeError} si sin nivel hay algún campo fuera del neutro.
 */
function exigirModeloNeutroSinNivel(modelo) {
    if (configuracionMultinivelEstaCompleta(modelo)) return;
    for (const campo of Object.keys(MODELO_NEUTRO_SIN_NIVEL)) {
        if (modelo?.[campo] !== MODELO_NEUTRO_SIN_NIVEL[campo]) {
            throw new TypeError(
                `[NivelesDePozo] configuración multinivel parcial: ${campo} = ${modelo?.[campo]} sin nivel asignado. Asigná el nivel y completá la configuración entera, o dejá el pozo como está`
            );
        }
    }
}

/**
 * Evalúa si una colección (los pozos suscritos a un sorteo) puede correr con
 * el motor multinivel: al menos un pozo, todos con configuración completa y
 * sin dos del mismo nivel. Es la puerta que JACK-1B/C debe consultar antes de
 * activar el camino nuevo; mientras diga que no, el motor vigente sigue.
 *
 * @returns {{lista: boolean, motivo: string|null, pendientes: Array}}
 */
function evaluarColeccionParaMultinivel(pozosDeLaColeccion) {
    const pozos = Array.isArray(pozosDeLaColeccion) ? pozosDeLaColeccion : [];
    if (pozos.length === 0) return { lista: false, motivo: 'sin_pozos', pendientes: [] };
    const pendientes = pozos.filter(p => !configuracionMultinivelEstaCompleta(p));
    if (pendientes.length > 0) return { lista: false, motivo: 'configuracion_pendiente', pendientes };
    try {
        exigirNivelesUnicosEnLaColeccion(pozos);
    } catch (_) {
        return { lista: false, motivo: 'nivel_duplicado', pendientes: [] };
    }
    // Un override de suscripción cambia el aporte TOTAL pero no el split del
    // pozo, así que visible + reserva dejarían de sumar el total y quedaría
    // dinero sin destino. JACK-1A lo prohíbe al guardar en modo multinivel, pero
    // la modalidad no se persiste: una colección legacy con override puede
    // quedar "completa" después, al recibir sus pozos un nivel.
    //
    // No se convierte ni se borra nada: retirar el override es una acción
    // explícita del operador, y hasta entonces la colección no entra.
    const conOverride = pozos.filter(p => p.porcentaje_aporte !== undefined && p.porcentaje_aporte !== null);
    if (conOverride.length > 0) {
        return { lista: false, motivo: 'override_de_aporte_no_permitido', pendientes: conOverride };
    }
    return { lista: true, motivo: null, pendientes: [] };
}

/**
 * Proyecta la colección de un sorteo en las cuatro posiciones (slots) que
 * pinta el admin más la lista de pozos con configuración pendiente.
 *
 * Cada slot solo ofrece pozos activos de SU nivel, así que dos pozos del
 * mismo nivel no pueden elegirse ni por error. Los pozos sin nivel no entran
 * en ningún slot: se listan aparte, con su suscripción vigente si la tienen,
 * porque el flujo actual sigue admitiéndolos.
 *
 * Es lógica de presentación pura para poder probarla sin DOM.
 *
 * @param {Array} pozosDisponibles filas de `pozos_comunes` visibles
 * @param {Array} suscripciones filas de `pozo_game_suscripciones` del sorteo
 */
function construirSlotsDeColeccion(pozosDisponibles, suscripciones) {
    const pozos = Array.isArray(pozosDisponibles) ? pozosDisponibles : [];
    const subs = Array.isArray(suscripciones) ? suscripciones : [];
    const suscripcionDe = pozo => subs.find(s => Number(s.pozo_id) === Number(pozo.id)) || null;

    const slots = NIVELES_DE_POZO.map(nivel => {
        const opciones = ordenarPozosPorNivel(
            pozos.filter(p => p.nivel === nivel && (p.activo === undefined || p.activo))
        );
        const seleccionado = opciones.find(p => suscripcionDe(p) !== null) || null;
        return {
            nivel,
            // `opciones` sigue PLANA: Lotowheel la recorre así para pintar sus
            // `<option>`. Lo agrupado se añade al lado, no la sustituye.
            opciones,
            opcionesPorGrupo: _agruparOpcionesPorGrupo(opciones),
            seleccionado,
            suscripcion: seleccionado ? suscripcionDe(seleccionado) : null,
        };
    });
    const pendientes = ordenarPozosPorNivel(pozos.filter(p => !configuracionMultinivelEstaCompleta(p))).map(pozo => ({
        pozo,
        suscripcion: suscripcionDe(pozo),
    }));
    const suscritos = pozos.filter(p => suscripcionDe(p) !== null);
    return {
        slots,
        pendientes,
        grupos: _gruposDePozosDisponibles(pozos),
        evaluacion: evaluarColeccionParaMultinivel(suscritos),
    };
}

/**
 * Identidad de un grupo para agrupar en el admin: ámbito + clave normalizada.
 * La misma regla que persiste el backend en `grupo_normalizado`, para que dos
 * grafías del mismo grupo («Lima», «  LIMA ») no se muestren como dos.
 */
function _claveDeGrupo(pozo) {
    if (typeof pozo.grupo !== 'string') return null;
    const limpio = pozo.grupo.normalize('NFC').trim().replace(/\s+/g, ' ');
    return limpio === '' ? null : `${pozo.nodo_id ?? 'global'}|${limpio.toLocaleLowerCase()}`;
}

/** Las opciones de un slot, repartidas por grupo; el cubo «sin grupo», al final. */
function _agruparOpcionesPorGrupo(opciones) {
    const porClave = new Map();
    for (const pozo of opciones) {
        const clave = _claveDeGrupo(pozo);
        if (!porClave.has(clave)) {
            porClave.set(clave, { grupo: clave === null ? null : pozo.grupo, opciones: [] });
        }
        porClave.get(clave).opciones.push(pozo);
    }
    const conGrupo = [...porClave.entries()].filter(([clave]) => clave !== null).map(([, valor]) => valor);
    const sinGrupo = porClave.get(null);
    conGrupo.sort((a, b) => String(a.grupo).localeCompare(String(b.grupo)));
    return sinGrupo ? [...conGrupo, sinGrupo] : conGrupo;
}

/**
 * Los grupos que el admin puede elegir de una vez, con el pozo que cubre cada
 * nivel. Un nivel que el grupo no cubre queda AUSENTE: quien elija el grupo
 * vacía ese hueco en vez de heredar el pozo del grupo anterior.
 *
 * @returns {Array<{grupo, nodoId, moneda, porNivel: Object, completa: boolean}>}
 */
function _gruposDePozosDisponibles(pozos) {
    const disponibles = pozos.filter(p => p.activo === undefined || p.activo);
    const porClave = new Map();
    for (const pozo of disponibles) {
        const clave = _claveDeGrupo(pozo);
        if (clave === null) continue;
        if (!porClave.has(clave)) {
            porClave.set(clave, {
                grupo: pozo.grupo,
                nodoId: pozo.nodo_id ?? null,
                moneda: pozo.moneda ?? null,
                porNivel: {},
            });
        }
        // El primer pozo de cada nivel manda: dos del mismo nivel en un grupo no
        // pueden coexistir (lo impide el índice único del ámbito).
        const grupo = porClave.get(clave);
        if (NIVELES_DE_POZO.includes(pozo.nivel) && !grupo.porNivel[pozo.nivel]) {
            grupo.porNivel[pozo.nivel] = pozo;
        }
    }
    return [...porClave.values()].map(grupo => ({
        ...grupo,
        completa: NIVELES_DE_POZO.every(nivel => Boolean(grupo.porNivel[nivel])),
    }));
}

/** Versión no lanzante de `exigirSplitCoherente`, para la validación en vivo del admin. */
function splitEsCoherente(pozo) {
    try {
        exigirSplitCoherente(pozo);
        return true;
    } catch (_) {
        return false;
    }
}

// ── ACARREO DEL APORTE ───────────────────────────────────────────────────────
//
// El aporte se trunca hoy por jugada aislada, así que una apuesta pequeña no
// aporta NADA: con cuatro niveles al 0,25 %, por debajo de 400 céntimos el pozo
// no recibe un solo céntimo. El acarreo guarda esa fracción como deuda durable
// y la materializa cuando completa un céntimo entero.

// Decimales admitidos en una tasa. Se declara PRIMERO y de él se deriva la
// escala: al revés, 0,125 % no cabría y se cobraría 0,13 % para siempre.
const DECIMALES_DE_TASA = 3;

// Unidades enteras por céntimo. 0,125 % de 1 céntimo son 0,00125 céntimos, así
// que la unidad es 1/100.000 de céntimo para que la aritmética sea exacta.
const ESCALA_DE_ACARREO = 100 * 10 ** DECIMALES_DE_TASA;

// Una tasa no puede superar el 100 % de la apuesta.
const TASA_MAXIMA = 100;

/**
 * Convierte una tasa porcentual en unidades enteras por céntimo apostado.
 *
 * Es frontera de dinero: `NaN`, `Infinity`, negativas y no numéricas DETIENEN
 * la jugada en vez de convertirse en un número. Y una tasa que no cabe exacta
 * en la precisión declarada se RECHAZA: redondearla cobraría de más en cada
 * jugada, para siempre, sin que nada lo delate.
 *
 * @throws {TypeError} si la tasa no es válida o excede la precisión.
 */
function tasaEnEnteros(pct) {
    if (typeof pct !== 'number' || !Number.isFinite(pct)) {
        throw new TypeError(`[Acarreo] tasa inválida: ${String(pct)}`);
    }
    if (pct < 0 || pct > TASA_MAXIMA) {
        throw new TypeError(`[Acarreo] tasa ${pct}% fuera de rango [0, ${TASA_MAXIMA}]`);
    }
    const escalado = pct * 10 ** DECIMALES_DE_TASA;
    const redondeado = Math.round(escalado);
    if (Math.abs(escalado - redondeado) > 1e-9) {
        throw new TypeError(`[Acarreo] la tasa ${pct}% no cabe en ${DECIMALES_DE_TASA} decimales`);
    }
    return redondeado;
}

/**
 * ¿Este pozo usa la política de acarreo?
 *
 * La política es del POZO y se activa al crearlo, nunca por tener nivel: un
 * pozo multinivel puede ser de otro juego.
 *
 * Conservar la fracción no es elegible: TODO pozo acumulado lo hace. Un par
 * `0/0` es el estado normal y un pozo nuevo nace así.
 *
 * `NULL/NULL` sigue devolviendo `false` SOLO durante la ventana de despliegue,
 * entre que sale este código y corre la migración v203 que convierte los
 * antiguos. Es deliberado y es lo que hace compatible el orden «primero
 * código, después backfill»: un pozo todavía sin migrar se comporta como antes
 * en vez de reventar en mitad de un sorteo. Tras la migración el par nulo no
 * existe —un trigger lo impide— y esta rama queda inalcanzable.
 *
 * Una propiedad AUSENTE, en cambio, es un bug: una proyección que olvidó
 * seleccionarlas. Devolver `false` ahí mandaría un pozo con acarreo al cálculo
 * viejo en silencio, así que se lanza.
 */
function elPozoUsaAcarreo(pozo) {
    if (pozo === null || typeof pozo !== 'object') {
        throw new TypeError(`[Acarreo] pozo inválido: ${String(pozo)}`);
    }
    const tieneVisible = Object.prototype.hasOwnProperty.call(pozo, 'deuda_visible');
    const tieneReserva = Object.prototype.hasOwnProperty.call(pozo, 'deuda_reserva');
    if (!tieneVisible || !tieneReserva) {
        throw new TypeError(
            `[Acarreo] la fila no trae las columnas de deuda (visible: ${tieneVisible}, reserva: ${tieneReserva}); ` +
                'una proyección incompleta NO es un pozo legacy'
        );
    }

    const visible = pozo.deuda_visible;
    const reserva = pozo.deuda_reserva;
    if (visible === null && reserva === null) return false;
    if (visible === null || reserva === null) {
        throw new TypeError(
            `[Acarreo] par de deudas incompleto: visible ${String(visible)}, reserva ${String(reserva)}`
        );
    }
    // `isSafeInteger`: un entero fuera del rango exacto de JS ya no cuenta
    // céntimos de forma fiable.
    if (!Number.isSafeInteger(visible) || !Number.isSafeInteger(reserva)) {
        throw new TypeError(`[Acarreo] la deuda debe ser entera segura: visible ${visible}, reserva ${reserva}`);
    }
    // Una deuda por destino puede ser NEGATIVA (se materializó de más ahí); el
    // invariante es que su SUMA, el resto pendiente, viva en [0, ESCALA).
    const suma = visible + reserva;
    if (suma < 0 || suma >= ESCALA_DE_ACARREO) {
        throw new TypeError(`[Acarreo] la suma de deudas ${suma} está fuera de [0, ${ESCALA_DE_ACARREO})`);
    }
    return true;
}

/**
 * Devengo de una apuesta en unidades de acarreo: apuesta × tasa entera.
 * Frontera de dinero: fuera del entero seguro no se redondea, se detiene.
 */
function devengoEnUnidades(apostadoCentimos, tasaEntera) {
    if (!Number.isSafeInteger(apostadoCentimos) || apostadoCentimos < 0) {
        throw new TypeError(`[Acarreo] apuesta inválida: ${String(apostadoCentimos)}`);
    }
    if (!Number.isSafeInteger(tasaEntera) || tasaEntera < 0 || tasaEntera > ESCALA_DE_ACARREO) {
        throw new TypeError(`[Acarreo] tasa entera inválida: ${String(tasaEntera)}`);
    }
    const unidades = apostadoCentimos * tasaEntera;
    if (!Number.isSafeInteger(unidades)) {
        throw new TypeError(`[Acarreo] devengo fuera del entero seguro: ${apostadoCentimos} × ${tasaEntera}`);
    }
    return unidades;
}

/**
 * Materializa el aporte REAL de una apuesta sobre la deuda vigente del pozo.
 *
 * Los mínimos son promesas congeladas al preparar (premio y R2 ya cuentan con
 * ellos): se asignan primero, descontando ESCALA de su PROPIA deuda. Sólo los
 * céntimos adicionales se reparten al destino con mayor deuda. Repartir todo
 * por mayor deuda retiraba un céntimo ya prometido cuando una deuda quedaba
 * negativa (visible −12.800 / reserva 24.800, alcanzable desde cero).
 *
 * La suma de los mínimos es el céntimo entero del devengo total —lo exige un
 * guard, no es una suposición— y la suma de deudas vive en [0, ESCALA), así que
 * quedan como mucho DOS céntimos adicionales y la suma final vuelve a
 * [0, ESCALA): el CHECK de la fila lo exige. Cada mínimo por separado NO es el
 * floor de su propio devengo: lo visible absorbe el resto de la división.
 *
 * @throws {TypeError} si un mínimo excede lo que su devengo garantiza: el
 *   payload congelado está malformado y no se materializa nada.
 */
function materializarAcarreo({
    deudaVisible,
    deudaReserva,
    devengoVisible,
    devengoReserva,
    minimoVisible,
    minimoReserva,
}) {
    for (const [nombre, valor] of Object.entries({
        deudaVisible,
        deudaReserva,
        devengoVisible,
        devengoReserva,
        minimoVisible,
        minimoReserva,
    })) {
        if (!Number.isSafeInteger(valor))
            throw new TypeError(`[Acarreo] ${nombre} debe ser entero seguro: ${String(valor)}`);
    }
    if (minimoVisible < 0 || minimoVisible * ESCALA_DE_ACARREO > devengoVisible) {
        throw new TypeError(
            `[Acarreo] mínimo visible ${minimoVisible} no garantizado por el devengo ${devengoVisible}`
        );
    }
    if (minimoReserva < 0 || minimoReserva * ESCALA_DE_ACARREO > devengoReserva) {
        throw new TypeError(
            `[Acarreo] mínimo de reserva ${minimoReserva} no garantizado por el devengo ${devengoReserva}`
        );
    }
    // La cota de DOS iteraciones del bucle no la daba ningún guard: los de
    // arriba admiten un mínimo muy por DEBAJO de lo devengado, y entonces el
    // bucle reparte céntimo a céntimo lo que el mínimo dejó sin asignar
    // (medido: con mínimo 0 sobre 50 céntimos devengados, 50 vueltas).
    //
    // Quien llama con acarreo (`_minimoGarantizadoConAcarreo`) SIEMPRE manda el
    // floor de cada destino —verificado sobre 120.000 combinaciones de tasa e
    // importe, cero excepciones—, así que exigir esa igualdad sería exacto para
    // el recorrido real. Se acota lo NO ASIGNADO en su lugar por dos razones,
    // y ninguna es «la igualdad rompería el acarreo productivo»: no lo rompe.
    //
    //  1. Es la propiedad que de verdad importa. Lo que alarga el bucle es el
    //     devengo sin asignar, no cómo se calculó el mínimo: con menos de dos
    //     céntimos sueltos da como mucho dos vueltas, sumando la deuda previa,
    //     que vive en [0, ESCALA).
    //  2. No ata esta función pura a la forma de repartir de UN llamador. La
    //     rama sin acarreo da a lo visible el resto de la división y no llega
    //     aquí; si mañana otro llamador reparte distinto pero deja menos de dos
    //     céntimos sueltos, es igual de sano y el contrato no debe rechazarlo.
    const devengoSinAsignar = devengoVisible + devengoReserva - (minimoVisible + minimoReserva) * ESCALA_DE_ACARREO;
    if (devengoSinAsignar >= 2 * ESCALA_DE_ACARREO) {
        throw new TypeError(
            `[Acarreo] los mínimos (${minimoVisible} + ${minimoReserva}) dejan ${devengoSinAsignar} unidades sin ` +
                `asignar, dos céntimos o más: el payload congelado no viene de los floors del devengo y el ` +
                'reparto recorrería el devengo céntimo a céntimo'
        );
    }

    let visible = minimoVisible;
    let reserva = minimoReserva;
    let dv = deudaVisible + devengoVisible - minimoVisible * ESCALA_DE_ACARREO;
    let dr = deudaReserva + devengoReserva - minimoReserva * ESCALA_DE_ACARREO;
    while (dv + dr >= ESCALA_DE_ACARREO) {
        if (dv >= dr) {
            visible += 1;
            dv -= ESCALA_DE_ACARREO;
        } else {
            reserva += 1;
            dr -= ESCALA_DE_ACARREO;
        }
    }
    return { visible, reserva, deudaVisible: dv, deudaReserva: dr };
}

// ── UMBRAL FINANCIABLE ───────────────────────────────────────────────────────
//
// Con nivel, el pozo solo dispara si la reserva ya cubre la base del ciclo
// siguiente (R2). Como el aporte es un porcentaje fijo, cada céntimo que sube
// el contador trae `tasaReserva / tasaVisible` céntimos de reserva: al sortear
// el umbral ya se sabe desde qué monto la reserva alcanza. Sortear desde ahí
// hace que R2 no bloquee nunca y que el pozo pague siempre antes de su máximo.
//
// Márgenes del redondeo del acarreo: cada deuda vive en [−0,5, 1,5) céntimos y
// la de reserva, tras el mínimo de la jugada evaluada, por debajo de 2,5. De
// ahí 3 céntimos de reserva y 2 de visible (medido: con 1 y 2 hay casos cortos).
const MARGEN_DE_RESERVA_POR_ACARREO_CENTIMOS = 3;
const MARGEN_DE_VISIBLE_POR_ACARREO_CENTIMOS = 2;

/** División entera hacia arriba sin depender del redondeo de la división flotante. */
function _divisionEnteraHaciaArriba(dividendo, divisor) {
    let cociente = Math.floor(dividendo / divisor);
    while (cociente * divisor < dividendo) cociente += 1;
    while (cociente > 0 && (cociente - 1) * divisor >= dividendo) cociente -= 1;
    return cociente;
}

// Tolerancia al multiplicar una base entera por un multiplicador REAL: 10 000 ×
// 2,51 da 25 099,999… en binario, y el piso a secas perdería un céntimo.
const TOLERANCIA_DE_MULTIPLICADOR = 1e-6;
// El admin propone multiplicadores con dos decimales.
const CENTESIMAS_POR_UNIDAD_DE_MULTIPLICADOR = 100;

function _redondeoSiEsEntero(valor) {
    const entero = Math.round(valor);
    return Math.abs(valor - entero) < TOLERANCIA_DE_MULTIPLICADOR ? entero : null;
}

/** Máximo del pozo en céntimos: la cifra de «Gana antes de $X». */
function techoDelPozo(pozoBase, maxMult) {
    const producto = pozoBase * maxMult;
    return _redondeoSiEsEntero(producto) ?? Math.floor(producto);
}

/** Piso del umbral en céntimos: `base × min_mult`, hacia arriba. */
function pisoDelPozo(pozoBase, minMult) {
    const producto = pozoBase * minMult;
    return _redondeoSiEsEntero(producto) ?? Math.ceil(producto);
}

/**
 * Cuánto tiene que subir el contador para que la reserva junte lo que le falta.
 * `Infinity` si la reserva no recibe nada: ningún monto la financia.
 */
function visibleQueFinanciaLaReserva(faltaDeReservaCentimos, porcentajeTotal, porcentajeReserva) {
    if (faltaDeReservaCentimos <= 0) return 0;
    const tasaReserva = tasaEnEnteros(porcentajeReserva);
    const tasaVisible = tasaEnEnteros(porcentajeTotal) - tasaReserva;
    if (tasaReserva <= 0) return Infinity;
    return (
        _divisionEnteraHaciaArriba(
            (faltaDeReservaCentimos + MARGEN_DE_RESERVA_POR_ACARREO_CENTIMOS) * tasaVisible,
            tasaReserva
        ) + MARGEN_DE_VISIBLE_POR_ACARREO_CENTIMOS
    );
}

/**
 * Primer monto del contador en que la reserva ya financia la base siguiente.
 * Se evalúa sobre el estado VIGENTE, así sirve igual al reiniciar, al crear y
 * al editar con un ciclo en curso.
 */
function montoMinimoFinanciable({ contador, reserva, overflow, base, porcentajeTotal, porcentajeReserva }) {
    const falta = base - (reserva + overflow);
    return contador + visibleQueFinanciaLaReserva(falta, porcentajeTotal, porcentajeReserva);
}

function _porcentajeLegible(valor) {
    return String(Number(valor.toFixed(DECIMALES_DE_TASA))).replace('.', ',');
}

/**
 * Exige que un pozo con nivel pueda pagar antes de su máximo en el peor caso
 * (arranca en la base, sin reserva ni overflow). Sin nivel la base la pone la
 * casa y no hay nada que exigir.
 *
 * @throws {TypeError} con el máximo mínimo y la reserva mínima que lo harían válido.
 */
function exigirTechoFinanciable({
    nivel,
    pozo_base: base,
    max_mult: maxMult,
    porcentaje_aporte_defecto: porcentajeTotal,
    porcentaje_aporte_reserva: porcentajeReserva,
}) {
    if (!configuracionMultinivelEstaCompleta({ nivel })) return;
    // Un pozo que no acumula (aporte 0) no promete nada: cero es un aporte válido.
    if (tasaEnEnteros(porcentajeTotal) === 0) return;
    const techo = techoDelPozo(base, maxMult);
    const necesario = base + visibleQueFinanciaLaReserva(base, porcentajeTotal, porcentajeReserva);
    if (techo >= necesario) return;

    const tasaTotal = tasaEnEnteros(porcentajeTotal);
    if (tasaEnEnteros(porcentajeReserva) <= 0) {
        throw new TypeError(
            `[NivelesDePozo] la reserva no puede ser 0 %: sin reserva el pozo nunca junta la base siguiente y no puede pagar antes de su máximo`
        );
    }
    // Máximo mínimo, en centésimas, que alcanza lo necesario.
    const centesimas = CENTESIMAS_POR_UNIDAD_DE_MULTIPLICADOR;
    let maxMultMinimo = Math.ceil((necesario / base) * centesimas) / centesimas;
    while (techoDelPozo(base, maxMultMinimo) < necesario) {
        maxMultMinimo = Math.round(maxMultMinimo * centesimas + 1) / centesimas;
    }
    // Reserva mínima (en la precisión de la tasa) con el máximo configurado.
    let tasaMinima = tasaEnEnteros(porcentajeReserva);
    let tasaMaxima = tasaTotal;
    const escalaDeTasa = 10 ** DECIMALES_DE_TASA;
    const alcanza = tasa => base + visibleQueFinanciaLaReserva(base, porcentajeTotal, tasa / escalaDeTasa) <= techo;
    if (alcanza(tasaMaxima)) {
        while (tasaMinima < tasaMaxima) {
            const medio = Math.floor((tasaMinima + tasaMaxima) / 2);
            if (alcanza(medio)) tasaMaxima = medio;
            else tasaMinima = medio + 1;
        }
    }
    const reservaMinima = alcanza(tasaMaxima) ? `${_porcentajeLegible(tasaMaxima / escalaDeTasa)} %` : null;
    throw new TypeError(
        `[NivelesDePozo] con una reserva de ${_porcentajeLegible(porcentajeReserva)} % el pozo no junta la base siguiente antes de su máximo: ` +
            `el máximo tiene que ser al menos ×${_porcentajeLegible(maxMultMinimo)}` +
            (reservaMinima
                ? `, o con el máximo ×${_porcentajeLegible(maxMult)} la reserva tiene que ser al menos ${reservaMinima} del aporte de ${_porcentajeLegible(porcentajeTotal)} % (lo visible baja en lo mismo)`
                : '')
    );
}

// Tolerancia al comparar porcentajes: son REAL y 0.25 + 0.25 no siempre da
// exactamente 0.5 en binario. Un céntimo de una apuesta grande queda muy por
// debajo de esto, así que no encubre un split mal declarado.
const TOLERANCIA_DE_PORCENTAJE = 1e-9;

/**
 * Exige que el split del aporte reparta EXACTAMENTE el total, sin dejar una
 * parte sin destino.
 *
 * Los tres son porcentajes absolutos de la apuesta: `porcentaje_aporte_defecto`
 * es cuánto cede la apuesta al pozo, y visible/reserva dicen cuánto de esa
 * apuesta va a cada lado. Por eso tienen que SUMAR el total, no sólo caber
 * dentro: con `defecto 2 / visible 0,25 / reserva 0,25` el 1,5 % restante no
 * tendría destino, y repartir proporcionalmente ese sobrante multiplicaría
 * ambas partidas por cuatro.
 *
 * Un pozo SIN nivel conserva la regla vigente: ambos en cero (el neutro), todo
 * el aporte va a lo visible y no se le exige la igualdad.
 *
 * @throws {TypeError} si con nivel la suma no es exactamente el total.
 */
function exigirSplitCoherente(pozo) {
    const total = Number(pozo?.porcentaje_aporte_defecto ?? 0);
    const visible = Number(pozo?.porcentaje_aporte_visible ?? 0);
    const reserva = Number(pozo?.porcentaje_aporte_reserva ?? 0);
    if (!configuracionMultinivelEstaCompleta(pozo)) {
        if (visible + reserva > total + TOLERANCIA_DE_PORCENTAJE) {
            throw new TypeError(
                `[NivelesDePozo] el split excede el aporte: visible ${visible} + reserva ${reserva} > total ${total}`
            );
        }
        return;
    }
    if (Math.abs(visible + reserva - total) > TOLERANCIA_DE_PORCENTAJE) {
        throw new TypeError(
            `[NivelesDePozo] el split debe repartir el aporte entero: visible ${visible} + reserva ${reserva} != total ${total}. ` +
                `Los tres son porcentajes de la apuesta; lo que sobra quedaría sin destino`
        );
    }
}

/**
 * Proyección PÚBLICA de un pozo. Declara su contrato campo a campo en vez de
 * copiar la fila: un spread publicaría umbral, reserva, overflow, porcentajes,
 * distribución y procedencia — todo lo que revela cuándo va a disparar y cómo
 * se financia.
 *
 * Es la única proyección para los endpoints que publican listas de pozos.
 *
 * @returns {{pozoId: string, nivel: string|null, nombre: string, actual: number, moneda: string}}
 */
function proyectarPozoPublico(fila, monedaPorDefecto = 'PEN') {
    const nivel = NIVELES_DE_POZO.includes(fila?.nivel) ? fila.nivel : null;
    return {
        pozoId: String(fila?.pozo_id ?? fila?.id ?? ''),
        nivel,
        nombre: fila?.pozo_nombre ?? fila?.nombre ?? '',
        actual: Number(fila?.pozo_actual ?? 0),
        moneda: fila?.moneda || monedaPorDefecto,
    };
}

const _api = {
    NIVELES_DE_POZO,
    ESTRATEGIAS_DE_DISTRIBUCION,
    CAMPOS_CONFIGURABLES_DEL_MODELO_DE_NIVELES,
    CAMPOS_DE_SALDO_DEL_MODELO_DE_NIVELES,
    MODELO_NEUTRO_SIN_NIVEL,
    PRESET_DE_REFERENCIA_PROPUESTA_A,
    ordenarPozosPorNivel,
    exigirNivelesUnicosEnLaColeccion,
    exigirNivelDePozo,
    configuracionMultinivelEstaCompleta,
    exigirModeloNeutroSinNivel,
    evaluarColeccionParaMultinivel,
    construirSlotsDeColeccion,
    splitEsCoherente,
    exigirSplitCoherente,
    proyectarPozoPublico,
    DECIMALES_DE_TASA,
    ESCALA_DE_ACARREO,
    TASA_MAXIMA,
    tasaEnEnteros,
    elPozoUsaAcarreo,
    devengoEnUnidades,
    materializarAcarreo,
    techoDelPozo,
    pisoDelPozo,
    visibleQueFinanciaLaReserva,
    montoMinimoFinanciable,
    exigirTechoFinanciable,
};

if (typeof module !== 'undefined' && module.exports) {
    module.exports = _api;
}

export {
    NIVELES_DE_POZO,
    ESTRATEGIAS_DE_DISTRIBUCION,
    CAMPOS_CONFIGURABLES_DEL_MODELO_DE_NIVELES,
    CAMPOS_DE_SALDO_DEL_MODELO_DE_NIVELES,
    MODELO_NEUTRO_SIN_NIVEL,
    PRESET_DE_REFERENCIA_PROPUESTA_A,
    ordenarPozosPorNivel,
    exigirNivelesUnicosEnLaColeccion,
    exigirNivelDePozo,
    configuracionMultinivelEstaCompleta,
    exigirModeloNeutroSinNivel,
    evaluarColeccionParaMultinivel,
    construirSlotsDeColeccion,
    splitEsCoherente,
    exigirSplitCoherente,
    proyectarPozoPublico,
    DECIMALES_DE_TASA,
    ESCALA_DE_ACARREO,
    TASA_MAXIMA,
    tasaEnEnteros,
    elPozoUsaAcarreo,
    devengoEnUnidades,
    materializarAcarreo,
    techoDelPozo,
    pisoDelPozo,
    visibleQueFinanciaLaReserva,
    montoMinimoFinanciable,
    exigirTechoFinanciable,
};
